import { IsClientContextProvider, NotificationProvider, Tooltip, } from '@showroomjs/ui';
import * as React from 'react';
import sections from 'react-showroom-sections';
import Wrapper from 'react-showroom-wrapper';
import { Div } from '../components/base';
import { Header } from '../components/header';
import { PageFallback } from '../components/page-fallback';
import { Sidebar } from '../components/sidebar';
import { CodeThemeContext } from '../lib/code-theme-context';
import { Suspense } from '../lib/lazy';
import { matchPath, Route, Routes, useLocation } from '../lib/routing';
import { getScrollFn } from '../lib/scroll-into-view';
import { MenuContextProvider } from '../lib/use-menu';
import { useSize } from '../lib/use-size';
import { TargetAudienceProvider } from '../lib/use-target-audience';
import { DefaultHomePage } from '../pages/index';
import { OfflinePage } from '../pages/offline';
import { hasCustomHomePage, routeMapping, routes } from '../route-mapping';
import { colorTheme, THEME } from '../theme';
export const ShowroomApp = () => {
    const location = useLocation();
    const lastPathName = React.useRef(location.pathname);
    React.useEffect(() => {
        const hashMatch = location.hash;
        if (hashMatch) {
            const hashTarget = document.getElementById(hashMatch.replace(/^#/, ''));
            if (hashTarget) {
                let isCurrent = true;
                getScrollFn().then((scroll) => {
                    if (isCurrent) {
                        scroll(hashTarget, {
                            scrollMode: 'if-needed',
                        });
                    }
                });
                return () => {
                    isCurrent = false;
                };
            }
        }
        else {
            if (lastPathName.current !== location.pathname) {
                window.scrollTo(0, 0);
                lastPathName.current = location.pathname;
            }
        }
    }, [location]);
    const matchedSection = React.useMemo(() => {
        for (const mapping of routeMapping) {
            const match = matchPath(mapping.path, location.pathname);
            if (match) {
                return mapping.section;
            }
        }
    }, [location.pathname]);
    const matchedTitle = React.useMemo(() => {
        if (matchedSection) {
            if (matchedSection.type === 'component') {
                return matchedSection.title;
            }
            if (matchedSection.type === 'markdown') {
                return matchedSection.frontmatter.title || matchedSection.fallbackTitle;
            }
        }
    }, [matchedSection]);
    const shouldHideHeader = matchedSection &&
        matchedSection.type === 'markdown' &&
        matchedSection.frontmatter.hideHeader;
    const shouldHideSidebar = !matchedSection ||
        (matchedSection &&
            matchedSection.type === 'markdown' &&
            matchedSection.frontmatter.hideSidebar);
    const headerRef = React.useRef(null);
    const headerSize = useSize(headerRef);
    const cssVariables = headerSize
        ? {
            '--header-height': `${headerSize.height}px`,
        }
        : undefined;
    return (React.createElement(Wrapper, null,
        location.hash && (React.createElement("style", null, `${location.hash} {
        color: ${THEME.colors['primary-800']};
      }`)),
        React.createElement(RootProviders, null,
            React.createElement(MenuContextProvider, { sections: sections },
                React.createElement(Div, { style: cssVariables },
                    !shouldHideHeader && React.createElement(Header, { ref: headerRef }),
                    React.createElement(Div, { css: { display: 'flex' } },
                        !shouldHideSidebar && React.createElement(Sidebar, { sections: sections }),
                        React.createElement(Suspense, { fallback: React.createElement(PageFallback, { title: matchedTitle }) },
                            React.createElement(Routes, null,
                                routes.map(function dataToRoute(route, i) {
                                    if (!route) {
                                        return React.createElement(React.Fragment, { key: i }, null);
                                    }
                                    const Ui = route.ui;
                                    if (Array.isArray(Ui)) {
                                        return (React.createElement(React.Fragment, { key: route.path }, Ui.map(dataToRoute)));
                                    }
                                    return (React.createElement(Route, { path: route.path, element: React.createElement(Ui, null), key: route.path }));
                                }),
                                React.createElement(Route, { path: "/_offline", element: React.createElement(OfflinePage, null) }),
                                !hasCustomHomePage && (React.createElement(Route, { path: "/", element: React.createElement(DefaultHomePage, null) }))))))))));
};
const RootProviders = (props) => {
    return (React.createElement(Tooltip.Provider, null,
        React.createElement(IsClientContextProvider, null,
            React.createElement(NotificationProvider, null,
                React.createElement(TargetAudienceProvider, null,
                    React.createElement(Div, { className: colorTheme },
                        React.createElement(CodeThemeContext.Provider, { value: THEME.codeTheme }, props.children)))))));
};
//# sourceMappingURL=showroom-app.js.map